import React from "react";
import {
  compareAsc,
  addDays,
  subDays,
  subMonths,
  addMonths,
  getMonth,
  getYear,
  startOfMonth,
  startOfDay,
  endOfMonth,
  isSameMonth,
  isThisMonth,
  setYear,
  setMonth,
} from "date-fns";
import { InputProps } from "../Input";
import { parseDateRange, parseDate } from "./utils/parseDates";
import { formatInputDateValue, formatDateRange } from "./utils/inputFormat";
import type { CalendarProps } from "./Calendar";

// Helper to call custom callbacks alongside default behaviour callbacks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Callback = (...args: any[]) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callAll = (...fns: Array<Callback | undefined>) => (...args: any[]) =>
  fns.forEach((fn) => typeof fn === "function" && fn(...args));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function exhaustiveSwitchCheck(x: never): never {
  throw new Error("Not all cases for the switch Statement");
}

enum DateRangeActionType {
  SelectStart,
  SelectEnd,
  SelectRange,
  ShowPicker,
  HidePicker,
  TogglePicker,
  SelectPrevCalendarMonth,
  SelectNextCalendarMonth,
  SelectPrevCalendarMonthWithPreviewDateUpdate,
  SelectNextCalendarMonthWithPreviewDateUpdate,
  SelectCalendarMonth,
  HandleRangeSelection,
  SetKeyboardDateToMonthStart,
  SetKeyboardDateToMonthEnd,
  IncrementKeyboardDateByDay,
  IncrementKeyboardDateByWeek,
  IncrementKeyboardDateByMonth,
  DecrementKeyboardDateByDay,
  DecrementKeyboardDateByWeek,
  DecrementKeyboardDateByMonth,
  SetPreviewMouseDate,
  RecalculatePreviewKeyboardDate,
  ResetPreviewDate,
  SetPreviewDate,
}

type DateRangeAction =
  | { type: DateRangeActionType.ResetPreviewDate }
  | { type: DateRangeActionType.SetPreviewDate; date: Date }
  | { type: DateRangeActionType.SetKeyboardDateToMonthStart }
  | { type: DateRangeActionType.SetKeyboardDateToMonthEnd }
  | { type: DateRangeActionType.IncrementKeyboardDateByDay }
  | { type: DateRangeActionType.IncrementKeyboardDateByWeek }
  | { type: DateRangeActionType.DecrementKeyboardDateByDay }
  | { type: DateRangeActionType.DecrementKeyboardDateByWeek }
  | { type: DateRangeActionType.IncrementKeyboardDateByMonth }
  | { type: DateRangeActionType.DecrementKeyboardDateByMonth }
  | { type: DateRangeActionType.RecalculatePreviewKeyboardDate }
  | { type: DateRangeActionType.SetPreviewMouseDate; cursor: Date | undefined }
  | { type: DateRangeActionType.SelectStart; rangeStart: Date | undefined }
  | { type: DateRangeActionType.SelectEnd; rangeEnd: Date | undefined }
  | { type: DateRangeActionType.HandleRangeSelection; date: Date }
  | {
      type: DateRangeActionType.SelectRange;
      rangeStart: Date | undefined;
      rangeEnd: Date | undefined;
    }
  | { type: DateRangeActionType.ShowPicker }
  | { type: DateRangeActionType.HidePicker }
  | { type: DateRangeActionType.TogglePicker }
  | {
      type: DateRangeActionType.SelectCalendarMonth;
      month: number;
      year: number;
    }
  | { type: DateRangeActionType.SelectPrevCalendarMonth }
  | { type: DateRangeActionType.SelectNextCalendarMonth }
  | { type: DateRangeActionType.SelectPrevCalendarMonthWithPreviewDateUpdate }
  | { type: DateRangeActionType.SelectNextCalendarMonthWithPreviewDateUpdate };

type DateRangeState = {
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  previewMouseDate?: Date;
  previewKeyboardDate?: Date;
  previewDate?: Date;
  displayDatePicker: boolean;
  selectedCalendarMonth: number;
  selectedCalendarYear: number;
};

const dateRangeReducer = (
  state: DateRangeState,
  action: DateRangeAction
): DateRangeState => {
  let calculatedPreviewKeyboardDate = state.previewKeyboardDate;
  let calculatedCalendarYear = state.selectedCalendarYear;
  let calculatedCalendarMonth = state.selectedCalendarMonth;
  let calculatedStartDate = state.selectedStartDate;
  let calculatedEndDate = state.selectedEndDate;
  let calculatedDisplayDatePicker = state.displayDatePicker;

  switch (action.type) {
    case DateRangeActionType.HandleRangeSelection:
      if (!state.selectedStartDate) {
        calculatedStartDate = action.date;
      }
      if (state.selectedStartDate && !state.selectedEndDate) {
        if (compareAsc(action.date, state.selectedStartDate) === -1) {
          calculatedEndDate = state.selectedStartDate;
          calculatedStartDate = action.date;
        } else {
          calculatedEndDate = action.date;
        }
        calculatedDisplayDatePicker = false;
      }

      if (state.selectedStartDate && state.selectedEndDate) {
        calculatedEndDate = undefined;
        calculatedStartDate = action.date;
      }

      return {
        ...state,
        selectedStartDate: calculatedStartDate,
        selectedEndDate: calculatedEndDate,
        displayDatePicker: calculatedDisplayDatePicker,
      };
    case DateRangeActionType.SetKeyboardDateToMonthStart:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = startOfMonth(state.previewKeyboardDate);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.SetKeyboardDateToMonthEnd:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = startOfDay(
          endOfMonth(state.previewKeyboardDate)
        );
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }
      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.IncrementKeyboardDateByDay:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = addDays(state.previewKeyboardDate, 1);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.IncrementKeyboardDateByWeek:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = addDays(state.previewKeyboardDate, 7);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.IncrementKeyboardDateByMonth:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = addMonths(state.previewKeyboardDate, 1);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.DecrementKeyboardDateByDay:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = subDays(state.previewKeyboardDate, 1);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.DecrementKeyboardDateByWeek:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = subDays(state.previewKeyboardDate, 7);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };
    case DateRangeActionType.DecrementKeyboardDateByMonth:
      if (state.previewKeyboardDate) {
        calculatedPreviewKeyboardDate = subMonths(state.previewKeyboardDate, 1);
        calculatedCalendarYear = getYear(calculatedPreviewKeyboardDate);
        calculatedCalendarMonth = getMonth(calculatedPreviewKeyboardDate);
      }

      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
        previewDate: calculatedPreviewKeyboardDate,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
      };

    case DateRangeActionType.SelectStart:
      return {
        ...state,
        selectedStartDate: action.rangeStart,
      };
    case DateRangeActionType.SelectEnd:
      return {
        ...state,
        selectedEndDate: action.rangeEnd,
      };
    case DateRangeActionType.SelectRange:
      return {
        ...state,
        selectedStartDate: action.rangeStart,
        selectedEndDate: action.rangeEnd,
      };
    case DateRangeActionType.ShowPicker:
      if (state.selectedStartDate) {
        calculatedCalendarYear = getYear(state.selectedStartDate);
        calculatedCalendarMonth = getMonth(state.selectedStartDate);

        if (
          state.previewKeyboardDate &&
          !isSameMonth(
            state.previewKeyboardDate,
            new Date(calculatedCalendarYear, calculatedCalendarMonth)
          )
        ) {
          calculatedPreviewKeyboardDate = state.selectedStartDate;
        }
      }
      return {
        ...state,
        displayDatePicker: true,
        selectedCalendarYear: calculatedCalendarYear,
        selectedCalendarMonth: calculatedCalendarMonth,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
      };
    case DateRangeActionType.HidePicker:
      return {
        ...state,
        displayDatePicker: false,
        previewDate: undefined,
      };
    case DateRangeActionType.TogglePicker:
      return {
        ...state,
        displayDatePicker: !state.displayDatePicker,
      };
    case DateRangeActionType.SelectPrevCalendarMonth:
      return {
        ...state,
        selectedCalendarMonth: getMonth(
          subMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        ),
        selectedCalendarYear: getYear(
          subMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        ),
      };
    case DateRangeActionType.SelectNextCalendarMonth:
      return {
        ...state,
        selectedCalendarMonth: getMonth(
          addMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        ),
        selectedCalendarYear: getYear(
          addMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        ),
      };
    case DateRangeActionType.SelectPrevCalendarMonthWithPreviewDateUpdate:
      if (state.previewKeyboardDate) {
        calculatedCalendarMonth = getMonth(
          subMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        );
        calculatedCalendarYear = getYear(
          subMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        );

        calculatedPreviewKeyboardDate = setYear(
          setMonth(state.previewKeyboardDate, calculatedCalendarMonth),
          calculatedCalendarYear
        );
      }
      return {
        ...state,
        selectedCalendarMonth: calculatedCalendarMonth,
        selectedCalendarYear: calculatedCalendarYear,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
      };
    case DateRangeActionType.SelectNextCalendarMonthWithPreviewDateUpdate:
      if (state.previewKeyboardDate) {
        calculatedCalendarMonth = getMonth(
          addMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        );
        calculatedCalendarYear = getYear(
          addMonths(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth),
            1
          )
        );

        calculatedPreviewKeyboardDate = setYear(
          setMonth(state.previewKeyboardDate, calculatedCalendarMonth),
          calculatedCalendarYear
        );
      }

      return {
        ...state,
        selectedCalendarMonth: calculatedCalendarMonth,
        selectedCalendarYear: calculatedCalendarYear,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
      };
    case DateRangeActionType.SelectCalendarMonth:
      return {
        ...state,
        selectedCalendarMonth: action.month,
        selectedCalendarYear: action.year,
      };

    case DateRangeActionType.RecalculatePreviewKeyboardDate:
      if (!state.previewKeyboardDate) {
        if (
          isThisMonth(
            new Date(state.selectedCalendarYear, state.selectedCalendarMonth)
          )
        ) {
          calculatedPreviewKeyboardDate = startOfDay(new Date());
        } else {
          calculatedPreviewKeyboardDate = new Date(
            state.selectedCalendarYear,
            state.selectedCalendarMonth
          );
        }
      }
      return {
        ...state,
        previewKeyboardDate: calculatedPreviewKeyboardDate,
      };

    case DateRangeActionType.SetPreviewMouseDate:
      return {
        ...state,
        previewMouseDate: action.cursor,
        previewDate: action.cursor,
      };
    case DateRangeActionType.ResetPreviewDate:
      return {
        ...state,
        previewDate: undefined,
      };
    case DateRangeActionType.SetPreviewDate:
      return {
        ...state,
        previewDate: !state.previewDate ? action.date : state.previewDate,
      };
    default: {
      return exhaustiveSwitchCheck(action);
    }
  }
};

type DateRangeHandler = {
  getCombinedInputProps: () => InputProps;
  getInputStartProps: () => InputProps;
  getInputEndProps: () => InputProps;
  getCalendarProps: () => CalendarProps;
  dispatch: React.Dispatch<DateRangeAction>;
};

type DateRangeStateAndHandler = Omit<
  DateRangeState,
  "selectedCalendarMonth" | "selectedCalendarYear"
> &
  DateRangeHandler;

const initialState = {
  selectedStartDate: undefined,
  selectedEndDate: undefined,
  previewMouseDate: undefined,
  previewKeyboardDate: undefined,
  previewDate: undefined,
  displayDatePicker: false,
  selectedCalendarMonth: undefined,
  selectedCalendarYear: undefined,
};
const useDateRange = (initialCalendarDate: Date): DateRangeStateAndHandler => {
  const dynamicStateInit = (initialDate: Date) => {
    return {
      ...initialState,
      selectedCalendarMonth: getMonth(initialDate),
      selectedCalendarYear: getYear(initialDate),
    };
  };
  const [
    {
      selectedStartDate,
      selectedEndDate,
      previewKeyboardDate,
      previewDate,
      displayDatePicker,
      selectedCalendarMonth,
      selectedCalendarYear,
    },
    dispatch,
  ] = React.useReducer(dateRangeReducer, initialState, () =>
    dynamicStateInit(initialCalendarDate)
  );

  const handleDateRangeParsing = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateRangeString = e.target.value;

    const [rangeStart, rangeEnd] = parseDateRange(dateRangeString);

    dispatch({
      type: DateRangeActionType.SelectRange,
      rangeStart,
      rangeEnd,
    });

    if (rangeStart) {
      dispatch({
        type: DateRangeActionType.SelectCalendarMonth,
        year: getYear(rangeStart),
        month: getMonth(rangeStart),
      });
    }

    if (rangeStart && rangeEnd) {
      if (compareAsc(rangeStart, rangeEnd) === 1) {
        dispatch({
          type: DateRangeActionType.SelectRange,
          rangeStart: rangeEnd,
          rangeEnd: rangeStart,
        });
        dispatch({
          type: DateRangeActionType.SelectCalendarMonth,
          year: getYear(rangeEnd),
          month: getMonth(rangeEnd),
        });
      }
    }
  };

  const handleDateRangeStartParsing = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rangeStartString = e.target.value;
    const rangeStart = parseDate(rangeStartString);

    if (rangeStart) {
      dispatch({
        type: DateRangeActionType.SelectStart,
        rangeStart,
      });
      dispatch({
        type: DateRangeActionType.SelectCalendarMonth,
        year: getYear(rangeStart),
        month: getMonth(rangeStart),
      });
    } else {
      dispatch({
        type: DateRangeActionType.SelectStart,
        rangeStart: undefined,
      });
    }
  };

  const handleDateRangeEndParsing = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rangeEndString = e.target.value;
    const rangeEnd = parseDate(rangeEndString);

    if (rangeEnd) {
      dispatch({
        type: DateRangeActionType.SelectEnd,
        rangeEnd,
      });
      if (selectedStartDate && compareAsc(selectedStartDate, rangeEnd) === 1) {
        dispatch({
          type: DateRangeActionType.SelectRange,
          rangeStart: rangeEnd,
          rangeEnd: selectedStartDate,
        });
        dispatch({
          type: DateRangeActionType.SelectCalendarMonth,
          year: getYear(rangeEnd),
          month: getMonth(rangeEnd),
        });
      }
      dispatch({ type: DateRangeActionType.HidePicker });
    } else {
      dispatch({
        type: DateRangeActionType.SelectEnd,
        rangeEnd: undefined,
      });
    }
  };

  const getInputStartProps = (props?: InputProps): InputProps => {
    const dateRangeStartString =
      selectedStartDate && formatInputDateValue(selectedStartDate);

    return {
      value: dateRangeStartString,
      ...props,
      onChange: callAll(props?.onChange, handleDateRangeStartParsing),
      onClick: callAll(props?.onClick, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
      onFocus: callAll(props?.onFocus, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
    };
  };

  const getInputEndProps = (props?: InputProps): InputProps => {
    const dateRangeEndString =
      selectedEndDate && formatInputDateValue(selectedEndDate);
    return {
      value: dateRangeEndString,
      ...props,
      onChange: callAll(props?.onChange, handleDateRangeEndParsing),
      onClick: callAll(props?.onClick, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
      onFocus: callAll(props?.onFocus, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
    };
  };

  const getCombinedInputProps = (props?: InputProps): InputProps => {
    const dateRangeString = formatDateRange(selectedStartDate, selectedEndDate);
    return {
      value: dateRangeString,
      ...props,
      onChange: callAll(props?.onChange, handleDateRangeParsing),
      onClick: callAll(props?.onClick, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
      onFocus: callAll(props?.onFocus, () =>
        dispatch({ type: DateRangeActionType.ShowPicker })
      ),
    };
  };

  const getCalendarProps = (): CalendarProps => {
    return {
      locale: "de-DE",
      firstDayOfWeek: 1,
      selectedStartDate,
      selectedEndDate,
      previewKeyboardDate,
      previewDate,
      selectedCalendarMonth,
      selectedCalendarYear,
    };
  };

  return {
    displayDatePicker,
    selectedStartDate,
    selectedEndDate,
    getCombinedInputProps,
    getInputStartProps,
    getInputEndProps,
    getCalendarProps,
    dispatch,
  };
};

export { DateRangeActionType, DateRangeAction, useDateRange, dateRangeReducer };
